import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Popover } from './Popover';
import styles from './VideoEditor.module.css';

/** ネイティブselectのOS依存メニューを使わず、エディタ共通のポップアップで選ぶ。 */
export function EditorSelect({ id, value, options, onChange, disabled, describedBy, label, variant = 'bordered', triggerLabel, onOpenChange, placement, inModal }: {
  id?: string;
  value: string;
  /** group を持つ option は、グループが切り替わる位置に見出しを出す (<optgroup> 相当)。disabled の option は選べない。
   *  icon はラベルの前に出す (話者の顔写真など)。選択中の option の icon はトリガーにも出す。 */
  options: { value: string; label: string; disabled?: boolean; group?: string; icon?: ReactNode }[];
  onChange: (value: string) => void;
  disabled?: boolean;
  describedBy?: string;
  label?: string;
  /** plain = 背景色・枠線なしの小さな「値 + シェブロン」だけのトリガー (演出プランの最大ダメ出し回数など)。 */
  variant?: 'bordered' | 'plain';
  /** トリガーに出す文言。未指定なら選択中の option の label (例: 回数の「最大2回」だけ選択肢の「2回」と変える)。 */
  triggerLabel?: string;
  /** メニューの開閉が変わったら呼ぶ (開いている間はトリガーのツールチップを抑止する等)。 */
  onOpenChange?: (open: boolean) => void;
  /** メニューをトリガーの上下どちらに優先して開くか (既定は下)。モーダルのフッタなどでは 'above'。 */
  placement?: 'below' | 'above';
  /** モーダルの中に置く (メニューをモーダルより手前に出す)。 */
  inModal?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const enabledIndexes = options.flatMap((option, index) => option.disabled ? [] : [index]);
  const menuOpen = open && !disabled;

  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  // 閉じる経路が複数 (選択・Esc・外側クリック・disabled 化) あるので、開閉の通知は state から一本で出す。
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useLayoutEffect(() => { onOpenChangeRef.current?.(menuOpen); }, [menuOpen]);
  useEffect(() => {
    if (menuOpen) optionRefs.current[selectedIndex >= 0 ? selectedIndex : enabledIndexes[0] ?? 0]?.focus({ preventScroll: true });
  }, [menuOpen, selectedIndex]);

  return <div ref={anchorRef} className={styles.dropdown}>
    <button id={id} ref={triggerRef} type="button" disabled={disabled}
      className={`${styles.dropdownTrigger} ${variant === 'plain' ? styles.dropdownTriggerPlain : styles.dropdownTriggerBordered}`}
      aria-label={label} aria-haspopup="listbox" aria-expanded={menuOpen} aria-describedby={describedBy}
      onClick={() => setOpen((current) => !current)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          setOpen(true);
        }
      }}>
      {options[selectedIndex]?.icon}
      <span className={styles.dropdownValue}>{triggerLabel ?? options[selectedIndex]?.label ?? value}</span>
      <ChevronDown size={12} aria-hidden="true" />
    </button>
    <Popover anchorRef={anchorRef} open={menuOpen} onClose={() => setOpen(false)} placement={placement} inModal={inModal}
      className={styles.dropdownMenu} role="listbox" ariaLabel={`${label ?? '項目'}の選択肢`}>
      {options.map((option, index) => <Fragment key={option.value}>
        {option.group && option.group !== options[index - 1]?.group && <div className={styles.dropdownGroupLabel} role="presentation">{option.group}</div>}
        <button type="button" role="option"
          ref={(element) => { optionRefs.current[index] = element; }}
          aria-selected={option.value === value} disabled={option.disabled} aria-disabled={option.disabled || undefined}
          className={`${styles.dropdownItem} ${option.value === value ? styles.dropdownItemSelected : ''}`}
          onKeyDown={(event) => {
            // 無効な選択肢は飛ばして移動する。
            const at = enabledIndexes.indexOf(index);
            const count = enabledIndexes.length;
            let next: number | undefined;
            if (event.key === 'ArrowDown') next = enabledIndexes[(at + 1) % count];
            if (event.key === 'ArrowUp') next = enabledIndexes[(at + count - 1) % count];
            if (event.key === 'Home') next = enabledIndexes[0];
            if (event.key === 'End') next = enabledIndexes[count - 1];
            if (next !== undefined) {
              event.preventDefault();
              optionRefs.current[next]?.focus();
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
              triggerRef.current?.focus({ preventScroll: true });
            }
          }}
          onClick={() => {
            onChange(option.value);
            setOpen(false);
            triggerRef.current?.focus({ preventScroll: true });
          }}>
          <span className={styles.dropdownCheck}>{option.value === value && <Check size={12} aria-hidden="true" />}</span>
          {option.icon}
          {option.label}
        </button>
      </Fragment>)}
    </Popover>
  </div>;
}
